/**
 * BusinessIdentitySection — Identity block for the Business entity dialog.
 *
 * Port of legacy SPFx `PersonIdentitySection.tsx` (Business branch). Unlike
 * the Individual section, customerType here is **manually picked** from a
 * dropdown of the 9 Business sub-types (business.md §1). The derivation hook
 * still runs to surface the NonProfit-flag-driven Call Class flip + the
 * supervisor-approval gate.
 *
 * Fields:
 *   - Legal Entity Name (Field 6/7; first+last split for Core Director)
 *   - DBA / Trade Name (Field 8, optional)
 *   - EIN (Field 13, masked to 9 digits — locked for IOLTA to 431355525)
 *   - Salutation (Field 5, read-only `*` per procedure)
 *   - CustomerType sub-type picker (disabled on existing-CIF)
 *   - Non-Profit flag checkbox (only visible when sub-type is Corporation)
 *   - NAICS code (Field 53, required) — locked to 813940 for Political
 *   - Business Industry / Occupation (Field 313, optional freeform)
 *   - Fiscal Year End (dropdown of common month-day values)
 *   - Consumer Account Flag (Field 39, read-only N)
 *   - Entity Type chip + supervisor-approval banner (shared pattern)
 *
 * IOLTA/Political banners render above the inputs so the officer sees the
 * fixed-value gotchas before they edit anything.
 */

import { useEffect } from 'react';
import { Pill } from '@/components/primitives';
import type { RaoPerson, RaoTinCode } from '../../../types';
import type { BusinessDerivationResult } from './useBusinessDerivation';
import {
  BUSINESS_SUB_TYPES,
  FISCAL_YEAR_END_OPTIONS,
  IOLTA_FIXED,
  IOLTA_SUBTYPES,
  NAICS_HELPER_TEXT,
  NONPROFIT_SUBTYPES,
  POLITICAL_FIXED,
  POLITICAL_SUBTYPES,
} from './business-config';

// -----------------------------------------------------------------------------
// Shared leaf styles (mirrored from IndividualIdentitySection for consistency)
// -----------------------------------------------------------------------------

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: 'var(--ink-600)',
  display: 'block',
  marginBottom: 4,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 4,
  border: '1px solid var(--ink-200)',
  fontSize: 14,
  boxSizing: 'border-box',
};

const readOnlyStyle: React.CSSProperties = {
  ...inputStyle,
  background: 'var(--field-readonly-bg, #f5f5f5)',
  color: 'var(--ink-700)',
};

function changedFieldStyle(base: React.CSSProperties, changed: boolean): React.CSSProperties {
  return changed
    ? { ...base, borderLeft: '3px solid var(--pcb-blue, #2968ad)', background: 'var(--field-changed-bg, #f0f7ff)', color: 'var(--ink-900, #1d1d1f)' }
    : base;
}

// -----------------------------------------------------------------------------
// Extra-field helpers — NAICS/SoS/fiscal-year live on `person` or `person.extra`
// -----------------------------------------------------------------------------

function readExtra(person: RaoPerson, key: string): string {
  const v = person.extra?.[key];
  return typeof v === 'string' ? v : '';
}

function patchExtra(person: RaoPerson, patch: Record<string, unknown>): Partial<RaoPerson> {
  return { extra: { ...(person.extra ?? {}), ...patch } };
}

// -----------------------------------------------------------------------------
// Props
// -----------------------------------------------------------------------------

export interface BusinessIdentitySectionProps {
  person: RaoPerson;
  onChange: (patch: Partial<RaoPerson>) => void;
  mode: 'new' | 'existing';
  isChanged?: (field: string) => boolean;
  derivation: BusinessDerivationResult;
  readOnly?: boolean;
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------

function derivationExplain(d: BusinessDerivationResult): string {
  switch (d.derivedFrom) {
    case 'picker':   return 'manual pick';
    case 'stored':   return 'existing CIF';
    case 'baseline': return 'new entity baseline';
    default:         return 'derived';
  }
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function BusinessIdentitySection({
  person,
  onChange,
  mode,
  isChanged,
  derivation,
  readOnly = false,
}: BusinessIdentitySectionProps): JSX.Element {
  const isExisting = mode === 'existing';
  const ic = isChanged ?? ((_: string) => false);

  const subType = (person.customerType ?? '') as string;
  const isIolta = IOLTA_SUBTYPES.has(subType);
  const noEin = person.tinCode === 'N';
  const isPolitical = POLITICAL_SUBTYPES.has(subType);
  const isCorporationPick = subType === 'Corporation' || subType === 'Corp';
  const showNonProfitFlag = isCorporationPick || NONPROFIT_SUBTYPES.has(subType);
  const isNonProfitFlag = Boolean(person.extra?.isNonProfit);

  // Locked-value enforcement:
  //   - IOLTA → EIN pinned to 43-1355525
  //   - Political → NAICS pinned to 813940
  //   - All Business types → TinCode 'E' + salutation '*' + consumerFlag false
  useEffect(() => {
    if (readOnly) return;
    const patch: Partial<RaoPerson> = {};

    // Salutation hardcoded to '*' per ENTITY-TYPE-PROCEDURE-ANALYSIS.md §1.
    // Store on extra since RaoPerson doesn't type `salutation` yet.
    if (person.extra?.salutation !== '*') {
      Object.assign(patch, patchExtra(person, { salutation: '*' }));
    }

    // TinCode = 'E' for Business (except DBA sole-prop which uses 'S'+SSN).
    // TODO(bill): DBA sole-prop vs business-DBA distinction. Card §1 shows
    // two Call Classes (25 biz-DBA / 26 sole-prop). For now we default to 'E'
    // and let the officer flip manually if sole-prop. Reconcile post-port.
    // 'N' = the officer ticked "No EIN / TIN" (Bill 2026-10-14): never override it.
    const wantTinCode: RaoTinCode = subType === 'DBA' ? (person.tinCode ?? 'E') : person.tinCode === 'N' ? 'N' : 'E';
    if (person.tinCode !== wantTinCode && subType !== 'DBA') {
      patch.tinCode = wantTinCode;
    }

    // consumerFlag — always false for Business (card §"Required fields" line).
    // TODO(bill): `consumerFlag` isn't a typed RaoPerson field yet — stash on
    // extra. Wave 3 promotes it. Core Director Field 39.
    const extraConsumer = person.extra?.consumerFlag;
    if (extraConsumer !== false) {
      Object.assign(patch, patchExtra(person, { consumerFlag: false }));
    }

    // IOLTA: pin EIN to the MO Lawyer Trust shared EIN.
    if (isIolta && person.taxId !== IOLTA_FIXED.taxId) {
      patch.taxId = IOLTA_FIXED.taxId;
    }

    // Political: pin NAICS to 813940 (Political Organizations).
    if (isPolitical && person.naicsCode !== POLITICAL_FIXED.naicsCode) {
      patch.naicsCode = POLITICAL_FIXED.naicsCode;
    }

    // Flush patch if anything to send. Skip when no-op to avoid infinite loop.
    const hasTopLevel = Object.keys(patch).some((k) => k !== 'extra');
    const hasExtra = patch.extra !== undefined;
    if (hasTopLevel || hasExtra) {
      onChange(patch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subType, isIolta, isPolitical, readOnly]);

  // Handler — picker change clears any locked fields that need to re-derive.
  function onSubTypeChange(next: string): void {
    const patch: Partial<RaoPerson> = {
      customerType: next as RaoPerson['customerType'],
    };
    // Clear EIN when leaving IOLTA so the officer re-enters; clear NAICS when
    // leaving Political.
    if (!IOLTA_SUBTYPES.has(next) && IOLTA_SUBTYPES.has(subType)) {
      patch.taxId = '';
    }
    if (!POLITICAL_SUBTYPES.has(next) && POLITICAL_SUBTYPES.has(subType)) {
      patch.naicsCode = '';
    }
    // Leaving Corporation + NonProfit flag on? Clear the flag.
    if (!(next === 'Corporation' || next === 'Corp') && isNonProfitFlag) {
      Object.assign(patch, patchExtra(person, { isNonProfit: false }));
    }
    onChange(patch);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Entity Type chip + derivation source */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '10px 12px',
          background: 'var(--info-bg)',
          border: '1px solid var(--info-line)',
          borderRadius: 6,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-700)' }}>
          Entity Type:
        </span>
        <Pill variant="info">{derivation.customerType}</Pill>
        <span
          style={{ fontSize: 11, color: 'var(--ink-600)' }}
          title={`Sub-type source: ${derivationExplain(derivation)}`}
        >
          ({derivationExplain(derivation)})
        </span>
        {derivation.promotedFrom && (
          <span style={{ fontSize: 11, color: 'var(--pcb-blue)', marginLeft: 'auto' }}>
            updated from &lsquo;{derivation.promotedFrom}&rsquo;
          </span>
        )}
      </div>

      {/* Supervisor-approval gate */}
      {derivation.requiresSupervisorApproval && (
        <div
          role="alert"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 13,
            color: 'var(--ink-900)',
          }}
        >
          <strong>Supervisor approval required.</strong> Corp / LLC / Partnership /
          Non-Profit new-CIF entries require a supervisor to approve the CDD + BO
          completeness check before save.
        </div>
      )}

      {/* IOLTA banner — fixed EIN warning */}
      {isIolta && (
        <div
          role="status"
          style={{
            padding: '10px 14px',
            background: 'var(--info-bg)',
            border: '1px solid var(--pcb-blue)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--ink-900)',
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--pcb-blue)', marginBottom: 4 }}>
            IOLTA — MO Lawyer Trust Account
          </div>
          EIN is locked to <strong>{IOLTA_FIXED.taxId}</strong> (MO Bar shared EIN).
          Interest will redirect to account <strong>{IOLTA_FIXED.interestAcctNumber}</strong>.
          NSF notices route to <strong>{IOLTA_FIXED.nsfNoticeName}</strong>.
        </div>
      )}

      {/* Political banner — NAICS + $50/day warning */}
      {isPolitical && (
        <div
          role="status"
          style={{
            padding: '10px 14px',
            background: 'var(--warn-bg)',
            border: '1px solid var(--warn-line)',
            borderRadius: 6,
            fontSize: 12,
            color: 'var(--ink-900)',
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 600, color: 'var(--warn-text)', marginBottom: 4 }}>
            Political Committee
          </div>
          NAICS locked to <strong>{POLITICAL_FIXED.naicsCode}</strong>.
          Daily cash-withdrawal limit: <strong>${POLITICAL_FIXED.dailyCashLimit}</strong>.
          {' '}Committee must request an account number BEFORE opening to file with
          MO Ethics Commission (pre-opening workflow gate — TODO(bill): wire
          pipeline step).
        </div>
      )}

      {/* Sub-type picker — disabled on existing-CIF per card §"Derivation rules" */}
      <div>
        <label style={labelStyle} htmlFor="rao-biz-subtype">
          Business sub-type <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
        </label>
        <select
          id="rao-biz-subtype"
          value={subType}
          onChange={(e) => onSubTypeChange(e.currentTarget.value)}
          disabled={readOnly || isExisting}
          style={changedFieldStyle(readOnly || isExisting ? readOnlyStyle : inputStyle, ic('customerType'))}
          title="Business entity sub-type (determines Call Class + required documents)"
        >
          <option value="">Select sub-type...</option>
          {BUSINESS_SUB_TYPES.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
        {subType && (
          <div style={{ fontSize: 11, color: 'var(--ink-600)', marginTop: 4, fontStyle: 'italic' }}>
            {BUSINESS_SUB_TYPES.find((s) => s.value === subType)?.hint}
          </div>
        )}
      </div>

      {/* Non-Profit flag — only meaningful on Corporation pick */}
      {showNonProfitFlag && isCorporationPick && (
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: 13,
            color: 'var(--ink-900)',
            cursor: readOnly ? 'not-allowed' : 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={isNonProfitFlag}
            onChange={(e) => onChange(patchExtra(person, { isNonProfit: e.currentTarget.checked }))}
            disabled={readOnly}
          />
          This Corporation is a Non-Profit (flips Call Class 20 → 21)
        </label>
      )}

      {/* Legal entity name + DBA row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-biz-legalname">
            Legal Entity Name (Field 6/7) <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
          </label>
          <input
            id="rao-biz-legalname"
            type="text"
            /* Legal name occupies firstName + lastName slots in the RaoPerson
               shape (Core Director Fields 6+7). We join on space for display
               and let the save dispatcher re-split on the trailing space when
               sending to CustAdd. TODO(bill): formalize name1/name2 split
               convention — card §"Required fields" table references
               firstName=Field 6 + lastName=Field 7. */
            value={[person.firstName ?? '', person.lastName ?? ''].filter(Boolean).join(' ')}
            onChange={(e) => {
              const val = e.currentTarget.value;
              const idx = val.indexOf(' ');
              if (idx < 0) {
                onChange({ firstName: val, lastName: '' });
              } else {
                onChange({ firstName: val.slice(0, idx), lastName: val.slice(idx + 1) });
              }
            }}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('firstName') || ic('lastName'))}
            placeholder="Acme Widgets, LLC"
            title="Legal entity name exactly as registered with the Secretary of State"
            maxLength={60}
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-biz-dba">DBA / Trade Name (Field 8)</label>
          <input
            id="rao-biz-dba"
            type="text"
            value={person.name2 ?? ''}
            onChange={(e) => onChange({ name2: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('name2'))}
            placeholder="Optional doing-business-as"
            title="DBA / Trade Name — second name slot on signature card for DBA entities"
            maxLength={40}
          />
        </div>
      </div>

      {/* EIN / Salutation / consumer-flag row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-biz-ein">
            EIN (Field 13){!noEin && <span style={{ color: 'var(--pcb-red, #b10505)' }}> *</span>}
            {isIolta && (
              <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--pcb-blue)' }}>
                (locked — IOLTA)
              </span>
            )}
          </label>
          <input
            id="rao-biz-ein"
            type="text"
            value={noEin ? '' : (person.taxId ?? '')}
            onChange={(e) =>
              onChange({ taxId: e.currentTarget.value.replace(/\D/g, '').slice(0, 9) })
            }
            readOnly={isExisting || readOnly || isIolta || noEin}
            style={changedFieldStyle(
              isExisting || readOnly || isIolta || noEin ? readOnlyStyle : inputStyle,
              ic('taxId'),
            )}
            maxLength={9}
            placeholder="123456789"
            title={isIolta ? 'IOLTA EIN is locked to MO Bar 43-1355525' : 'Employer Identification Number (9 digits, no dash)'}
          />
          {/* Bill 2026-10-14: no EIN of its own → TIN code N; Core Director is searched by the entity
              name first, then the business gets a bank-assigned customer number. Not for IOLTA. */}
          {!isIolta && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 6, color: 'var(--ink-800)' }}>
              <input
                type="checkbox"
                checked={noEin}
                disabled={isExisting || readOnly}
                onChange={(e) => onChange(e.currentTarget.checked ? { tinCode: 'N', taxId: '' } : { tinCode: 'E' })}
              />
              No EIN / TIN
            </label>
          )}
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-biz-salutation">Salutation (F5)</label>
          <input
            id="rao-biz-salutation"
            type="text"
            value="*"
            readOnly
            disabled
            style={readOnlyStyle}
            title="Business salutation is always '*' per procedure"
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-biz-consumer">Consumer Flag (F39)</label>
          <input
            id="rao-biz-consumer"
            type="text"
            value="N — Non-Consumer"
            readOnly
            disabled
            style={readOnlyStyle}
            title="Always N for business entities"
          />
        </div>
      </div>

      {/* NAICS + Business Industry row */}
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-biz-naics">
            NAICS Code (Field 53) <span style={{ color: 'var(--pcb-red, #b10505)' }}>*</span>
            {isPolitical && (
              <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--pcb-blue)' }}>
                (locked)
              </span>
            )}
          </label>
          <input
            id="rao-biz-naics"
            type="text"
            value={person.naicsCode ?? ''}
            onChange={(e) =>
              onChange({ naicsCode: e.currentTarget.value.replace(/\D/g, '').slice(0, 6) })
            }
            readOnly={readOnly || isPolitical}
            style={changedFieldStyle(
              readOnly || isPolitical ? readOnlyStyle : inputStyle,
              ic('naicsCode'),
            )}
            maxLength={6}
            placeholder="6-digit NAICS"
            title={NAICS_HELPER_TEXT}
          />
        </div>
        <div>
          <label style={labelStyle} htmlFor="rao-biz-industry">
            Business Industry / Occupation (Field 313)
          </label>
          <input
            id="rao-biz-industry"
            type="text"
            value={person.occupation ?? ''}
            onChange={(e) => onChange({ occupation: e.currentTarget.value })}
            readOnly={readOnly}
            style={changedFieldStyle(readOnly ? readOnlyStyle : inputStyle, ic('occupation'))}
            placeholder="e.g., Retail bakery, Law firm, Construction contractor"
            maxLength={30}
          />
        </div>
      </div>

      {/* Fiscal year-end */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={labelStyle} htmlFor="rao-biz-fye">Fiscal Year End</label>
          <select
            id="rao-biz-fye"
            value={readExtra(person, 'fiscalYearEnd')}
            onChange={(e) => onChange(patchExtra(person, { fiscalYearEnd: e.currentTarget.value }))}
            disabled={readOnly}
            style={readOnly ? readOnlyStyle : inputStyle}
            title="Fiscal year-end (month-day) — defaults to December 31 for most businesses"
          >
            {FISCAL_YEAR_END_OPTIONS.map((f) => (
              <option key={f.value || 'none'} value={f.value}>{f.label}</option>
            ))}
          </select>
        </div>
      </div>

      {isPolitical && (
        <div style={{ fontSize: 11, color: 'var(--ink-600)', fontStyle: 'italic' }}>
          TODO(bill): Political sub-type requires reserving an account number
          BEFORE opening to file with MO Ethics Commission. Pipeline step not
          yet wired — flag in business.md §"Open questions" #5.
        </div>
      )}
    </div>
  );
}
